import React, { CSSProperties, useState } from 'react';
import styled from 'styled-components';
import {
  autoUpdate,
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { IconName, SvgIcon } from '../../Icon';
import { theme } from '../../../styles/theme';
import findIcon from '../../Icon/findIcon';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import { FloatingLayer } from '../../FloatingManager';
import BorderedDropdownLabel from '../../Dropdown/BorderedDropdownLabel';

interface IProps {
  value: IconName;
  onChange: (value: IconName) => void;
  icons: IconName[];
  customWidth?: CSSProperties['minWidth'];
}

const ListWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  padding: 8px;
  ${floatingComponentStyles};
`;

const IconPicker = ({ value, onChange, icons, customWidth }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(2), flip()],
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleSelect = (icon: IconName) => {
    onChange(icon);
    setIsOpen(false);
  };

  const selectedValue = findIcon(value);

  return (
    <>
      <BorderedDropdownLabel
        ref={refs.setReference}
        {...getReferenceProps()}
        $customWidth={customWidth}
      >
        <SvgIcon
          name={selectedValue}
          width={24}
          height={24}
          customFill="currentColor"
        />
      </BorderedDropdownLabel>
      {isOpen && (
        <FloatingLayer>
          <ListWrapper
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {icons.map((icon) => (
              <SvgIcon
                key={icon}
                name={icon}
                width={24}
                height={24}
                isHoverable={true}
                customFill={theme.colors.secondary900}
                onClick={() => handleSelect(icon)}
                isActive={selectedValue === icon}
              />
            ))}
          </ListWrapper>
        </FloatingLayer>
      )}
    </>
  );
};

export default IconPicker;
